import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Routes, Route, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import "@/App.css";
import { PageWait, Shell } from "./lib/ui";
import { maxDisclosureOf, navItemAllowed, disclosureRank, appOpenRoute } from "./lib/master";
import { navItemAllowedForApp, appOpenRouteForApp } from "./lib/g1Journey";
import ApplicationOverview from "./components/ApplicationOverview";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import ContactSales from "./pages/ContactSales";
import SsoBroker from "./pages/Sso";
import Logout from "./pages/Logout";
import AdminPlatform from "./pages/AdminPlatform";
import AdminTenant from "./pages/AdminTenant";
import AdminConsoleGate from "./components/AdminConsoleGate";
import { me, logout, getPortfolio, getApp, authorizeMdr, addEvidence, ingestEvidence, reassess as reassessMdr, deployApp, closeG8, createApplication, updateWhy, updateRecommendation, decideG1 } from "./lib/api";
import { withApp } from "./lib/story";
import { homeFor } from "./lib/admin";
import Program from "./pages/Program";
import Portfolio from "./pages/Portfolio";
import Applications from "./pages/Applications";
import Assessment from "./pages/Assessment";
import Cockpit from "./pages/Cockpit";
import Evidence from "./pages/Evidence";
import Decision from "./pages/Decision";
import Governance from "./pages/Governance";
import Delivery from "./pages/Delivery";
import Enterprise from "./pages/Enterprise";
import Lifecycle from "./pages/Lifecycle";
import Tour from "./components/Tour";
import AppErrorBoundary from "./components/AppErrorBoundary";

function appIdFromSearch(search) {
  return new URLSearchParams(search || "").get("app") || "";
}

function GuestWorkspaceRedirect() {
  const location = useLocation();
  const next = `${location.pathname}${location.search || ""}`;
  return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
}

function Workspace({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [currentAppId, setCurrentAppId] = useState(
    () => appIdFromSearch(window.location.search) || "APP-042"
  );
  const [app, setApp] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const fetchGen = useRef(0);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const loadPortfolio = useCallback(async () => {
    try {
      const { data } = await getPortfolio();
      setPortfolio(data);
    } catch (err) {
      if (err.response?.status === 401) onLogoutRef.current();
    }
  }, []);

  const loadApp = useCallback(async (id) => {
    if (!id) return;
    const gen = ++fetchGen.current;
    try {
      const { data } = await getApp(id);
      if (gen !== fetchGen.current) return;
      setApp(data);
    } catch (err) {
      if (gen !== fetchGen.current) return;
      if (err.response?.status === 401) onLogoutRef.current();
    }
  }, []);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  useEffect(() => {
    const requested = appIdFromSearch(location.search);
    if (requested && requested !== currentAppId) {
      setCurrentAppId(requested);
    }
  }, [location.search, currentAppId]);

  useEffect(() => {
    loadApp(currentAppId);
  }, [currentAppId, loadApp]);

  const selectApp = useCallback((id) => {
    if (!id) return;
    const dest = location.pathname === "/"
      ? appOpenRouteForApp(user?.guide || user, app)
      : location.pathname;
    setCurrentAppId(id);
    navigate(withApp(dest, id));
  }, [location.pathname, navigate, user, app]);

  const reloadWorkspace = useCallback(async () => {
    await Promise.all([loadApp(currentAppId), loadPortfolio()]);
  }, [currentAppId, loadApp, loadPortfolio]);

  const handleAuthorize = useCallback(async (payload) => {
    await authorizeMdr(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleReassess = useCallback(async (payload) => {
    await reassessMdr(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleAddEvidence = useCallback(async (payload) => {
    const { data } = await addEvidence(currentAppId, payload);
    await loadApp(currentAppId);
    return data;
  }, [currentAppId, loadApp]);
  const handleIngest = useCallback(async (payload) => {
    const { data } = await ingestEvidence(currentAppId, payload);
    await loadApp(currentAppId);
    return data;
  }, [currentAppId, loadApp]);
  const handleDeploy = useCallback(async (payload = {}) => {
    await deployApp(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleCloseG8 = useCallback(async (payload = {}) => {
    await closeG8(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleCreateApp = useCallback(async (payload) => {
    const { data } = await createApplication(payload);
    const created = data?.application || data;
    const id = created?.id;
    await loadPortfolio();
    if (id) {
      setCurrentAppId(id);
      await loadApp(id);
      navigate(withApp("/overview", id));
    }
    return created;
  }, [loadApp, loadPortfolio, navigate]);
  const handleSaveWhy = useCallback(async (payload) => {
    await updateWhy(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleSaveRecommendation = useCallback(async (payload) => {
    await updateRecommendation(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const handleDecideG1 = useCallback(async (payload) => {
    await decideG1(currentAppId, payload);
    await reloadWorkspace();
  }, [currentAppId, reloadWorkspace]);
  const reloadApp = useCallback(() => loadApp(currentAppId), [currentAppId, loadApp]);
  const startTour = useCallback(() => setTourOpen(true), []);
  const closeTour = useCallback(() => setTourOpen(false), []);

  const waiting = !app;
  const maxLevel = maxDisclosureOf(user);
  const pathAllowed = navItemAllowedForApp(location.pathname, user?.guide || user, app);

  return (
    <Shell user={user} portfolio={portfolio} app={app} appId={currentAppId} onLogout={onLogout} onStartTour={disclosureRank(maxLevel) >= 3 ? startTour : undefined}>
      {pathAllowed ? (
        <Outlet
          context={{
            user,
            portfolio,
            app,
            waiting,
            selectApp,
            startTour,
            handleAuthorize,
            handleReassess,
            handleAddEvidence,
            handleIngest,
            handleDeploy,
            handleCloseG8,
            handleCreateApp,
            handleSaveWhy,
            handleSaveRecommendation,
            handleDecideG1,
            reloadWorkspace,
            reloadApp,
          }}
        />
      ) : (
        <Navigate to={withApp(user?.guide?.home || user?.home || "/", currentAppId)} replace />
      )}
      {tourOpen && pathAllowed && <Tour onClose={closeTour} onSelectApp={setCurrentAppId} appId={currentAppId} />}
    </Shell>
  );
}

function useWorkspace() {
  return useOutletContext();
}

function PortfolioPage() {
  const { portfolio, startTour, user } = useWorkspace();
  return <Portfolio portfolio={portfolio} onStartTour={startTour} user={user} />;
}

function ApplicationsPage() {
  const { portfolio, user, handleCreateApp } = useWorkspace();
  return <Applications portfolio={portfolio} user={user} onCreateApp={handleCreateApp} />;
}

function ProgramPage() {
  const { user } = useWorkspace();
  return <Program user={user} />;
}

function LifecyclePage() {
  const { waiting, app, portfolio } = useWorkspace();
  return waiting ? <PageWait inline /> : <Lifecycle app={app} portfolio={portfolio} />;
}

function OverviewPage() {
  const { waiting, app, portfolio, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <ApplicationOverview app={app} portfolio={portfolio} user={user} />;
}

function AssessmentPage() {
  const { waiting, app, handleAddEvidence, handleIngest, handleSaveWhy, handleDecideG1, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Assessment app={app} onAddEvidence={handleAddEvidence} onIngest={handleIngest} onSaveWhy={handleSaveWhy} onDecideG1={handleDecideG1} user={user} />;
}

function CockpitPage() {
  const { waiting, app, portfolio, selectApp, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Cockpit app={app} portfolio={portfolio} onSelectApp={selectApp} user={user} />;
}

function EvidencePage() {
  const { waiting, app, handleAddEvidence, handleIngest, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Evidence app={app} onAddEvidence={handleAddEvidence} onIngest={handleIngest} user={user} />;
}

function DecisionPage() {
  const { waiting, app, portfolio, handleAuthorize, handleReassess, handleSaveRecommendation, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Decision app={app} portfolio={portfolio} onAuthorize={handleAuthorize} onReassess={handleReassess} onSaveRecommendation={handleSaveRecommendation} user={user} />;
}

function GovernancePage() {
  const { waiting, app, portfolio, reloadWorkspace, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Governance app={app} portfolio={portfolio} onReload={reloadWorkspace} user={user} />;
}

function DeliveryPage() {
  const { waiting, app, portfolio, handleDeploy, handleCloseG8, reloadApp, user } = useWorkspace();
  return waiting ? <PageWait inline /> : <Delivery app={app} portfolio={portfolio} onDeploy={handleDeploy} onCloseG8={handleCloseG8} onReload={reloadApp} user={user} />;
}

function EnterprisePage() {
  const { waiting, app } = useWorkspace();
  return waiting ? <PageWait inline /> : <Enterprise app={app} />;
}

function workspaceRoutes(user, onLogout) {
  return (
    <Route element={<Workspace user={user} onLogout={onLogout} />}>
      <Route path="/" element={<PortfolioPage />} />
      <Route path="/applications" element={<ApplicationsPage />} />
      <Route path="/program" element={<ProgramPage />} />
      <Route path="/lifecycle" element={<LifecyclePage />} />
      <Route path="/overview" element={<OverviewPage />} />
      <Route path="/assessment" element={<AssessmentPage />} />
      <Route path="/cockpit" element={<CockpitPage />} />
      <Route path="/evidence" element={<EvidencePage />} />
      <Route path="/decision" element={<DecisionPage />} />
      <Route path="/governance" element={<GovernancePage />} />
      <Route path="/delivery" element={<DeliveryPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  );
}

function Session() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const boot = useCallback(async () => {
    try {
      const { status, data } = await me();
      const ok = status === 200;
      setUser(ok ? data : false);
      if (ok) {
        try {
          const { trackVisitorEvent } = await import("./lib/visitorAnalytics");
          trackVisitorEvent("workspace_entered", { console: data.console || "workspace" });
        } catch {
          /* analytics must not block session */
        }
      }
      return ok;
    } catch {
      setUser(false);
      return false;
    }
  }, []);
  useEffect(() => { boot(); }, [boot]);

  const clearSession = useCallback(async () => {
    try { await logout(); } catch { /* ignore */ }
    setUser(false);
  }, []);

  const requestSignOut = useCallback(() => {
    navigate("/logout");
  }, [navigate]);

  if (user === null) return <div className="loading-screen">Loading governed workspace…</div>;

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact-sales" element={<ContactSales />} />
        <Route path="/login" element={<Login onLogin={boot} />} />
        <Route path="/register" element={<Register onLogin={boot} />} />
        <Route path="/signup" element={<Register onLogin={boot} />} />
        <Route path="/login/sso" element={<SsoBroker onLogin={boot} />} />
        <Route path="/logout" element={<Logout signedIn={false} onClear={clearSession} />} />
        <Route path="/applications" element={<GuestWorkspaceRedirect />} />
        <Route path="/overview" element={<GuestWorkspaceRedirect />} />
        <Route path="/assessment" element={<GuestWorkspaceRedirect />} />
        <Route path="/evidence" element={<GuestWorkspaceRedirect />} />
        <Route path="/cockpit" element={<GuestWorkspaceRedirect />} />
        <Route path="/decision" element={<GuestWorkspaceRedirect />} />
        <Route path="/enterprise" element={<GuestWorkspaceRedirect />} />
        <Route path="/program" element={<GuestWorkspaceRedirect />} />
        <Route path="/lifecycle" element={<GuestWorkspaceRedirect />} />
        <Route path="/governance" element={<GuestWorkspaceRedirect />} />
        <Route path="/delivery" element={<GuestWorkspaceRedirect />} />
        <Route path="/admin/*" element={<GuestWorkspaceRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const home = homeFor(user);
  const consoleKind = user.console || "workspace";

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={home} replace />} />
      <Route path="/register" element={<Navigate to={home} replace />} />
      <Route path="/signup" element={<Navigate to={home} replace />} />
      <Route path="/pricing" element={<Navigate to={home} replace />} />
      <Route path="/contact-sales" element={<Navigate to={home} replace />} />
      <Route path="/login/sso" element={<Navigate to={home} replace />} />
      <Route path="/logout" element={<Logout signedIn onClear={clearSession} />} />
      {consoleKind === "platform" && (
        <>
          <Route path="/admin/platform/tenants/:code" element={<AdminPlatform user={user} onLogout={requestSignOut} />} />
          <Route path="/admin/platform/:panel" element={<AdminPlatform user={user} onLogout={requestSignOut} />} />
          <Route path="/admin/platform" element={<AdminPlatform user={user} onLogout={requestSignOut} />} />
          <Route path="*" element={<Navigate to="/admin/platform" replace />} />
        </>
      )}
      {consoleKind === "tenant" && (
        <>
          <Route path="/admin/tenant/:panel" element={<AdminTenant user={user} onLogout={requestSignOut} />} />
          <Route path="/admin/tenant" element={<AdminTenant user={user} onLogout={requestSignOut} />} />
          <Route path="/admin/platform/*" element={<AdminConsoleGate user={user} kind="platform" />} />
          <Route path="/admin/*" element={<Navigate to="/admin/tenant" replace />} />
          {workspaceRoutes(user, requestSignOut)}
        </>
      )}
      {consoleKind !== "platform" && consoleKind !== "tenant" && (
        <>
          <Route path="/admin/platform/*" element={<AdminConsoleGate user={user} kind="platform" />} />
          <Route path="/admin/tenant/*" element={<AdminConsoleGate user={user} kind="tenant" />} />
          <Route path="/admin/*" element={<Navigate to="/" replace />} />
          {workspaceRoutes(user, requestSignOut)}
        </>
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppErrorBoundary>
        <Session />
      </AppErrorBoundary>
    </BrowserRouter>
  );
}
